import {
    DivWithSpacing,
    EnumFilterExpressionOperation,
    EnumOperation,
    FilterExpression,
    FlexRow,
    Select,
    SelectOption,
    DateInput,
    toCalendarDay,
} from '@synqo/frontend-component-library';
import { ApiExportRangeFilterOption, ApiExportRangeFilterOptionPreset } from '@Model';
import { useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { FilterOptionProps } from '@Modules/Exports/FilterOptionProps';
import dayjs from 'dayjs';

const getPresetConfig = (preset: ApiExportRangeFilterOptionPreset): { from: string; to: string } => {
    const now = dayjs();
    const day = (date: dayjs.Dayjs) => toCalendarDay(date.toDate());
    switch (preset) {
        case ApiExportRangeFilterOptionPreset.Last30Days:
            return { from: day(now.subtract(30, 'days')), to: day(now) };
        case ApiExportRangeFilterOptionPreset.LastMonth:
            return {
                from: day(now.subtract(1, 'month').startOf('month')),
                to: day(now.subtract(1, 'month').endOf('month')),
            };
        case ApiExportRangeFilterOptionPreset.CurrentMonth:
            return { from: day(now.startOf('month')), to: day(now.endOf('month')) };
    }
};

const getPresetConfigLabel = (preset: ApiExportRangeFilterOptionPreset): string => {
    switch (preset) {
        case ApiExportRangeFilterOptionPreset.Last30Days:
            return 'Letzte 30 Tage';
        case ApiExportRangeFilterOptionPreset.LastMonth:
            return 'Letzter Monat';
        case ApiExportRangeFilterOptionPreset.CurrentMonth:
            return 'Aktueller Monat';
    }
};

export function RangeFilterOption(props: FilterOptionProps<ApiExportRangeFilterOption>) {
    const { filterOption, setFilterExpression, setIsValidFilterExpression } = props;
    const [from, setFrom] = useState<string>('');
    const [to, setTo] = useState<string>('');
    const [preset, setPreset] = useState<ApiExportRangeFilterOptionPreset | null>(null);

    const updateFilterExpression = ({ from, to }: { from: string; to: string }): boolean => {
        const expression: FilterExpression = {
            operation: EnumFilterExpressionOperation.And,
            filters: [
                { field: filterOption.field, value: from, operator: EnumOperation.GreaterEqual },
                { field: filterOption.field, value: to, operator: EnumOperation.LessEqual },
            ],
        };
        setFilterExpression(expression);
        return true;
    };

    const onChangeFrom = (value: string) => {
        setFrom(() => value);
        validateFrom(value);
        updateFilterExpression({ from: value, to });
        setPreset(() => null);
        return true;
    };

    const onChangeTo = (value: string) => {
        setTo(() => value);
        validateTo(value);
        updateFilterExpression({ from, to: value });
        setPreset(() => null);
        return true;
    };

    const validateTo = (newTo?: string | null) => {
        if (!newTo) {
            setIsValidFilterExpression(false);
            return;
        }
        if (!from) {
            setIsValidFilterExpression(false);
            return;
        }

        const fromDate = new Date(from);
        const toDate = new Date(to);

        if (fromDate > toDate) {
            setIsValidFilterExpression(false);
            return;
        }

        setIsValidFilterExpression(true);
        return;
    };

    const validateFrom = (newFrom?: string | null) => {
        if (!newFrom) {
            setIsValidFilterExpression(false);
            return;
        }
        if (!to) {
            setIsValidFilterExpression(false);
            return;
        }

        const fromDate = new Date(newFrom);
        const toDate = new Date(to);

        if (fromDate > toDate) {
            setIsValidFilterExpression(false);
            return;
        }

        setIsValidFilterExpression(true);
        return;
    };

    const presets: SelectOption<ApiExportRangeFilterOptionPreset | null>[] = useMemo(
        () =>
            filterOption.presets.length
                ? [
                      { name: 'custom', value: null, label: 'Benutzerdefiniert' },
                      ...filterOption.presets.map((preset) => {
                          const label = getPresetConfigLabel(preset);
                          return { name: label, value: preset, label };
                      }),
                  ]
                : [],
        [filterOption.presets],
    );

    useEffectOnce(() => {
        updateFilterExpression({ from, to });
        setIsValidFilterExpression(false);
    });

    const onSetPreset = (value: ApiExportRangeFilterOptionPreset | null) => {
        if (value === preset) {
            return true;
        }

        if (value === null) {
            setPreset(() => null);
            return true;
        }

        const newPreset = filterOption.presets.find((preset) => preset === value);
        if (newPreset === undefined) {
            return false;
        }
        const presetConfig = getPresetConfig(newPreset);
        setFrom(presetConfig.from);
        setTo(presetConfig.to);
        setPreset(() => newPreset);
        updateFilterExpression({ from: presetConfig.from, to: presetConfig.to });
        setIsValidFilterExpression(true);
        return true;
    };

    return (
        <DivWithSpacing>
            <FlexRow>
                <DateInput
                    type="day"
                    key="from"
                    name="from"
                    label="Von"
                    value={from}
                    onChange={onChangeFrom}
                    required
                />
                <DateInput type="day" key="to" name="to" label="Bis" value={to} onChange={onChangeTo} required />
            </FlexRow>
            {presets.length && (
                <FlexRow>
                    <Select<ApiExportRangeFilterOptionPreset | null>
                        name="preset"
                        items={presets}
                        asRadioButtons
                        value={preset}
                        onChange={onSetPreset}
                    />
                </FlexRow>
            )}
        </DivWithSpacing>
    );
}
